<script setup lang="ts">
import { BannerArtwork } from "@/models/Banner";

const store = useBigHeroBannerStore();

const props = defineProps<{
  index: number;
  noTransition?: boolean;
}>();

const banner = computed(() => store.banners[props.index] as BannerArtwork);
const tabIndex = computed(() =>
  store.currentSlideIndex === props.index ? "0" : "-1",
);

let bgLoaded = false;
let albumLoaded = false;

const emit = defineEmits<{
  (e: "load"): void;
}>();

function onLoad(loaded: "album" | "bg") {
  if (loaded === "album") {
    albumLoaded = true;
  }
  if (loaded === "bg") {
    bgLoaded = true;
  }

  if (albumLoaded && bgLoaded) {
    emit("load");
  }
}

const internalLink = computed(
  () => /^\//.test(banner.value.url) || /^\.\.\//.test(banner.value.url),
);
</script>

<template>
  <BigHeroBannerSlide
    :index="index"
    class="slide"
  >
    <BigHeroBannerBackgroundImg
      :img="banner.img"
      :alt="$t('SV.LIT_BackgroundImageTo', { title: banner.text.title })"
      :no-lazy="true"
      :no-transition="noTransition"
      @load="onLoad('bg')"
    />

    <div class="slide-container">
      <div class="slide-cover">
        <div class="slide-cover-image-wrapper">
          <Image
            v-if="banner.albumImg"
            :src="banner.albumImg"
            :alt="$t('SV.LIT_AlbumCover', { title: banner.text.title })"
            rounded
            :restrict-width="225"
            :no-lazy="true"
            :no-transition="noTransition"
            @load="onLoad('album')"
          />
        </div>
      </div>
      <div class="slide-text">
        <div>
          <span
            class="slide-cat"
            :style="{ 'background-color': banner.categoryBgColor }"
          >
            {{ banner.text.category }}
          </span>
        </div>
        <h2 class="slide-title">
          {{ banner.text.title }}
        </h2>
        <p class="slide-subtitle">
          {{ banner.text.subtitle }}
        </p>
        <div>
          <Button
            :to="(internalLink && banner.url) || undefined"
            :href="(!internalLink && banner.url) || undefined"
            :target="(!internalLink && '_blank') || undefined"
            variant="transparent-white"
            :tabindex="tabIndex"
            icon-end="fa-light fa-chevron-right"
            class="hero-banner-feature-button"
            :data-name="'CALLOUT: ' + banner.text.title"
          >
            {{ banner.text.cta
            }}<span class="sr-only">
              "{{ banner.text.title }}: {{ banner.text.subtitle }}"</span
            >
          </Button>
        </div>
      </div>
    </div>
  </BigHeroBannerSlide>
</template>

<style scoped lang="postcss">
.slide {
  @apply flex items-center justify-center text-light-100;
  &::before {
    content: "";
    @apply absolute bottom-0 left-0 right-0 top-0 block;
    background-color: rgba(50, 50, 50, 0.85);
    z-index: -1;
  }
}
.slide-container {
  @apply grid gap-5 px-10 py-4 text-center;

  @media screen(md) {
    @apply flex w-full items-center gap-20 px-20 text-left;
  }
}
.slide-cover {
  flex: 1.5;
}
.slide-cover-image-wrapper {
  height: 175px;
  width: 175px;
  @apply mx-auto;
  @media screen(md) {
    height: 225px;
    width: 225px;
    @apply ml-auto;
  }
}
h2 {
  @apply font-display text-xl font-bold text-light-100 md:text-3xl lg:text-4xl xl:text-5xl;
}
.slide-cat {
  @apply inline-block rounded px-3 py-1 font-display text-xs uppercase;
}
.slide-text {
  @apply grid md:gap-3;
  flex: 3;
}
.slide-title {
  @apply max-md:mt-1;
}
.slide-subtitle {
  @apply max-md:mb-3;
}
</style>
